import { useEffect, useState } from 'react';

// Custom hook for other components to safely use FreshChat
export const useFreshChat = () => {
  const [isReady, setIsReady] = useState(!!window.fcWidget);
  const [widget, setWidget] = useState(window.fcWidget || null);

  useEffect(() => {
    const handleReady = (event) => {
      setIsReady(true);
      setWidget(event.detail.widget);
    };

    // Listen for the ready event
    window.addEventListener('freshchat:ready', handleReady);

    // Check if already ready
    if (window.fcWidget && typeof window.fcWidget.init === 'function') {
      setIsReady(true);
      setWidget(window.fcWidget);
    }

    return () => {
      window.removeEventListener('freshchat:ready', handleReady);
    };
  }, []);

  return {
    isReady,
    widget,
    // Safe methods that check if widget is ready
    user: {
      get: (callback) => {
        if (isReady && widget) {
          return widget.user.get(callback);
        }
        console.warn('FreshChat widget not ready yet');
        return Promise.reject(new Error('FreshChat widget not ready'));
      },
      setProperties: (properties) => {
        if (isReady && widget) {
          return widget.user.setProperties(properties);
        }
        console.warn('FreshChat widget not ready yet');
      },
      clear: () => {
        if (isReady && widget) {
          return widget.user.clear();
        }
        console.warn('FreshChat widget not ready yet');
        return Promise.reject(new Error('FreshChat widget not ready'));
      }
    },
    on: (event, callback) => {
      if (isReady && widget) {
        return widget.on(event, callback);
      }
      console.warn('FreshChat widget not ready yet');
    }
  };
};

const FreshchatWidget = () => {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Only load Freshchat if it hasn't been loaded already
    if (window.fcWidget) {
      console.log('FreshChat widget already exists');
      setIsReady(true);
      return;
    }

    console.log('Loading FreshChat widget...');

    // Add global error handler for FreshChat
    const originalErrorHandler = window.onerror;
    window.onerror = function (message, source, lineno, colno, error) {
      if (source && source.includes('762750.js')) {
        console.error('FreshChat script error:', {
          message,
          source,
          lineno,
          colno,
          error
        });
        setError(`FreshChat script error: ${message}`);
      }
      // Call original error handler if it exists
      if (originalErrorHandler) {
        return originalErrorHandler(message, source, lineno, colno, error);
      }
      return false;
    };

    // Add unhandled promise rejection handler for FreshChat
    const originalRejectionHandler = window.onunhandledrejection;
    window.onunhandledrejection = function (event) {
      if (event.reason && event.reason.toString().includes('FreshChat')) {
        console.error('FreshChat unhandled promise rejection:', event.reason);
        setError(`FreshChat promise rejection: ${event.reason}`);
      }
      // Call original handler if it exists
      if (originalRejectionHandler) {
        return originalRejectionHandler(event);
      }
    };

    // Monitor for any FreshChat related global variables
    const checkForFreshChatGlobals = () => {
      console.log('Checking for FreshChat globals:', {
        fcWidget: window.fcWidget,
        fcWidgetInit: window.fcWidgetInit,
        initFreshChat: window.initFreshChat,
        Freshchat: window.Freshchat
      });
    };

    // Check immediately and after script loads
    checkForFreshChatGlobals();

    // Create script element
    const script = document.createElement('script');
    script.src = '//eu.fw-cdn.com/12777158/762750.js';
    script.setAttribute('chat', 'true');
    script.async = true;

    // Add timeout for script loading
    const scriptTimeout = setTimeout(() => {
      console.error('FreshChat script loading timeout');
      setError('FreshChat script loading timeout');
    }, 10000); // 10 second timeout

    // Wait for script to load and initialize
    script.onload = () => {
      clearTimeout(scriptTimeout);
      console.log('FreshChat script loaded, waiting for initialization...');

      // Check for FreshChat globals after script loads
      checkForFreshChatGlobals();

      // Check if there are any immediate errors
      if (window.fcWidget) {
        console.log('FreshChat widget immediately available:', window.fcWidget);
      } else {
        console.log(
          'FreshChat widget not immediately available, checking for errors...'
        );
      }

      // Wait a bit more for the widget to fully initialize
      const checkWidget = () => {
        if (window.fcWidget && typeof window.fcWidget.init === 'function') {
          console.log('FreshChat widget loaded and ready');
          setIsReady(true);

          // Dispatch a custom event to notify other components
          window.dispatchEvent(
            new CustomEvent('freshchat:ready', {
              detail: { widget: window.fcWidget }
            })
          );
        } else if (window.fcWidget && !window.fcWidget.init) {
          console.log(
            'FreshChat widget exists but init method not available yet, retrying...'
          );
          setTimeout(checkWidget, 100);
        } else {
          console.log('FreshChat widget not ready yet, retrying...');
          setTimeout(checkWidget, 100);
        }
      };

      // Start checking after a short delay
      setTimeout(checkWidget, 100);
    };

    script.onerror = (error) => {
      clearTimeout(scriptTimeout);
      console.error('Failed to load FreshChat script:', error);
      setError('Failed to load FreshChat script');
    };

    // Insert script into DOM
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      // Remove script if component unmounts
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
      // Restore original error handlers
      window.onerror = originalErrorHandler;
      window.onunhandledrejection = originalRejectionHandler;
    };
  }, []);

  // This component doesn't render anything visible
  return null;
};

export default FreshchatWidget;
